import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Categories = ({ token }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'expense',
    budget_amount: '',
    color: '#6366F1',
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingCategory) {
        await axios.put(
          `${BACKEND_URL}/api/categories/${editingCategory.id}`,
          {
            name: formData.name,
            budget_amount: parseFloat(formData.budget_amount) || 0,
            color: formData.color,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Category updated successfully');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/categories`,
          {
            ...formData,
            budget_amount: parseFloat(formData.budget_amount) || 0,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Category created successfully');
      }

      setShowModal(false);
      setEditingCategory(null);
      setFormData({
        name: '',
        type: 'expense',
        budget_amount: '',
        color: '#6366F1',
      });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      type: category.type,
      budget_amount: category.budget_amount,
      color: category.color,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const incomeCategories = categories.filter(cat => cat.type === 'income');
  const expenseCategories = categories.filter(cat => cat.type === 'expense');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-state">
        <p className="font-body text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight text-foreground">
            Categories
          </h1>
          <p className="mt-1 text-base font-body text-muted-foreground">
            Manage your income and expense categories
          </p>
        </div>
        <Button onClick={() => setShowModal(true)} data-testid="add-category-button">
          <Plus className="h-4 w-4 mr-2" />
          Add Category
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-sm shadow-sm p-6">
          <h2 className="text-2xl font-heading font-medium mb-4 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-green-500"></span>
            Income Categories
          </h2>
          <div className="space-y-2">
            {incomeCategories.length === 0 ? (
              <p className="text-center text-muted-foreground font-body py-8">No income categories</p>
            ) : (
              incomeCategories.map((category) => (
                <div
                  key={category.id}
                  className="flex items-center justify-between p-4 border border-border rounded-sm hover:bg-muted/50 transition-colors"
                  data-testid="category-item"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="h-4 w-4 rounded-sm"
                      style={{ backgroundColor: category.color }}
                    />
                    <span className="font-body font-medium text-foreground">{category.name}</span>
                    {category.is_predefined && (
                      <span className="text-xs font-mono text-muted-foreground uppercase">
                        (Predefined)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(category)}
                      data-testid="edit-category-button"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {!category.is_predefined && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(category.id)}
                        data-testid="delete-category-button"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm shadow-sm p-6">
          <h2 className="text-2xl font-heading font-medium mb-4 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-red-500"></span>
            Expense Categories
          </h2>
          <div className="space-y-2">
            {expenseCategories.length === 0 ? (
              <p className="text-center text-muted-foreground font-body py-8">No expense categories</p>
            ) : (
              expenseCategories.map((category) => (
                <div
                  key={category.id}
                  className="flex items-center justify-between p-4 border border-border rounded-sm hover:bg-muted/50 transition-colors"
                  data-testid="category-item"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div
                      className="h-4 w-4 rounded-sm"
                      style={{ backgroundColor: category.color }}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-body font-medium text-foreground">{category.name}</span>
                        {category.is_predefined && (
                          <span className="text-xs font-mono text-muted-foreground uppercase">
                            (Predefined)
                          </span>
                        )}
                      </div>
                      {category.budget_amount > 0 && (
                        <div className="flex items-center gap-1 mt-1 text-xs font-mono text-muted-foreground">
                          <DollarSign className="h-3 w-3" />
                          Budget: ${category.budget_amount.toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(category)}
                      data-testid="edit-category-button"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {!category.is_predefined && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(category.id)}
                        data-testid="delete-category-button"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent data-testid="category-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editingCategory ? 'Edit Category' : 'Add Category'}
            </DialogTitle>
            <DialogDescription className="font-body">
              {editingCategory ? 'Update category details' : 'Create a new income or expense category'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="font-body text-sm font-medium">
                Category Name
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                disabled={editingCategory?.is_predefined}
                className="mt-1"
                data-testid="category-name-input"
              />
            </div>

            {!editingCategory && (
              <div>
                <Label htmlFor="type" className="font-body text-sm font-medium">
                  Type
                </Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="category-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {(formData.type === 'expense' || editingCategory?.type === 'expense') && (
              <div>
                <Label htmlFor="budget_amount" className="font-body text-sm font-medium">
                  Budget Amount (Optional)
                </Label>
                <Input
                  id="budget_amount"
                  type="number"
                  step="0.01"
                  value={formData.budget_amount}
                  onChange={(e) => setFormData({ ...formData, budget_amount: e.target.value })}
                  className="mt-1 font-mono"
                  data-testid="budget-amount-input"
                />
              </div>
            )}

            <div>
              <Label htmlFor="color" className="font-body text-sm font-medium">
                Color
              </Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  id="color"
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-20 h-10"
                  data-testid="color-input"
                />
                <Input
                  type="text"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="flex-1 font-mono"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} data-testid="cancel-button">
                Cancel
              </Button>
              <Button type="submit" data-testid="save-category-button">
                {editingCategory ? 'Update' : 'Create'} Category
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Categories;
